/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiPanel,
  EuiPopover,
  EuiSearchBar,
  EuiSkeletonText,
  EuiSpacer,
  Query,
  SearchFilterConfig,
} from "@inscopix/ideas-eui";
import { Tool, useAllToolsQuery } from "./ToolBrowser.api";
import { Fragment, useMemo, useState } from "react";
import { ToolCard } from "./ToolCard";
import { CallOutError } from "components/CallOutError/CallOutError";
import { VList } from "virtua";
import { PromptFirstCustomTool } from "./PromptFirstCustomTool";
import { isNonNull } from "utils/isNonNull";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { chain } from "lodash";
import { ToolVisibility } from "types/constants";

type Sort =
  | "name-asc"
  | "created-asc"
  | "created-desc"
  | "updated-asc"
  | "updated-desc";

interface SortFilterProps {
  sort: Sort;
  setSort: (sort: Sort) => void;
}

const SortFilter = ({ sort, setSort }: SortFilterProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const items: { name: string; value: Sort }[] = [
    { name: "Newest", value: "created-desc" },
    { name: "Oldest", value: "created-asc" },
    { name: "Recently updated", value: "updated-desc" },
    { name: "Least recently updated", value: "updated-asc" },
    { name: "Name", value: "name-asc" },
  ];

  return (
    <EuiPopover
      style={{ borderLeft: "1px solid #d7dee8" }}
      isOpen={isPopoverOpen}
      closePopover={() => setIsPopoverOpen(false)}
      button={
        <EuiButtonEmpty
          color="text"
          iconSide="right"
          iconType="arrowDown"
          onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
        >
          Sort
        </EuiButtonEmpty>
      }
    >
      <EuiContextMenuPanel
        size="s"
        items={items.map((item) => (
          <EuiContextMenuItem
            key={item.value}
            onClick={() => setSort(item.value)}
            icon={item.value === sort ? "check" : "empty"}
          >
            {item.name}
          </EuiContextMenuItem>
        ))}
      />
    </EuiPopover>
  );
};

type Scope = "custom" | "all";

interface ScopeFilterProps {
  scope: Scope;
  setScope: (scope: Scope) => void;
}

const ScopeFilter = ({ scope, setScope }: ScopeFilterProps) => {
  return (
    <EuiButtonGroup
      legend="Scope filter"
      options={[
        {
          id: "all",
          label: "All",
          css: css`
            border-start-end-radius: 0px !important;
            border-end-end-radius: 0px !important;
          `,
        },
        { id: "custom", label: "Custom" },
      ]}
      idSelected={scope}
      onChange={(scope) => setScope(scope as Scope)}
      buttonSize="m"
    />
  );
};

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;
  `,
};

interface ToolBrowserProps {
  tenantId: number;
  tenantKey: string;
}

export const ToolBrowser = ({ tenantId, tenantKey }: ToolBrowserProps) => {
  const { data, isLoading, error } = useAllToolsQuery();
  const [query, setQuery] = useState<Query>();
  const [scope, setScope] = useState<Scope>("all");
  const [sort, setSort] = useState<Sort>("created-desc");
  const isCustomTool = data?.some((tool) => tool.tenant?.id === tenantId);

  const allVisibleTools = useMemo(
    () =>
      (data ?? []).filter(
        (tool) =>
          tool.visibility === ToolVisibility.PROTECTED ||
          tool.visibility === ToolVisibility.PUBLIC ||
          tool.tenant?.id === tenantId,
      ),
    [data, tenantId],
  );

  const filteredTools = useMemo(() => {
    // Remove tools from other tenants
    let tools = allVisibleTools;

    if (query) {
      tools = Query.execute(query, tools);
    }

    return tools
      .filter((tool) => {
        switch (scope) {
          case "custom":
            return tool.visibility === ToolVisibility.PRIVATE;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        switch (sort) {
          case "name-asc":
            if (a.name < b.name) {
              return -1;
            } else if (a.name > b.name) {
              return 1;
            } else {
              return 0;
            }
          case "created-asc":
            return (
              new Date(a.created).getTime() - new Date(b.created).getTime()
            );
          case "created-desc":
            return (
              new Date(b.created).getTime() - new Date(a.created).getTime()
            );
          case "updated-asc":
            return (
              new Date(a.created).getTime() - new Date(b.created).getTime()
            );
          case "updated-desc":
            return (
              new Date(b.created).getTime() - new Date(a.created).getTime()
            );
        }
        // ESLint array-callback-return doesn't detect exhaustive switch cases
        return 0;
      });
  }, [allVisibleTools, query, scope, sort]);

  const searchFilters: SearchFilterConfig[] = useMemo(
    () => [
      {
        type: "custom_component",
        component: () => <ScopeFilter scope={scope} setScope={setScope} />,
      },
      {
        type: "field_value_selection",
        field: "user.email",
        name: "Author",
        multiSelect: "or",
        options: chain(allVisibleTools)
          .map((tool) => tool.user)
          .filter(isNonNull)
          .uniqBy((user) => user.id)
          .map((user) => {
            const fullName = `${user.first_name} ${user.last_name}`;
            return {
              value: user.email,
              name: fullName,
              view: <UserAvatar userId={user.id} displayName size="s" />,
            };
          })
          .value(),
      },
      {
        type: "field_value_selection",
        field: "tags" satisfies keyof Tool,
        name: "Tag",
        multiSelect: "or",
        options: chain(allVisibleTools)
          .flatMap((c) => c.tags)
          .filter(isNonNull)
          .uniq()
          .map((tag) => {
            return {
              value: tag,
              name: tag,
            };
          })
          .value(),
      },
      {
        type: "custom_component",
        component: () => <SortFilter sort={sort} setSort={setSort} />,
      },
    ],
    [allVisibleTools, scope, sort],
  );

  return (
    <EuiFlexGroup css={styles.root} direction="column" gutterSize="l">
      <EuiFlexItem grow={false}>
        <EuiSearchBar
          query={query}
          onChange={({ query }) => setQuery(query ?? undefined)}
          filters={searchFilters}
          box={{
            placeholder: "Find a tool...",
            incremental: false,
          }}
        />
      </EuiFlexItem>

      <EuiFlexItem style={{ overflow: "hidden" }}>
        {(() => {
          if (isLoading) {
            return <EuiSkeletonText lines={5} />;
          } else if (error) {
            return <CallOutError />;
          } else if (scope === "custom" && !isCustomTool) {
            return (
              <PromptFirstCustomTool
                tenantId={tenantId}
                tenantKey={tenantKey}
              />
            );
          } else if (filteredTools.length === 0) {
            return (
              <EuiPanel hasBorder>
                <EuiEmptyPrompt
                  icon={<EuiIcon type="indexMapping" size="xl" />}
                  title={<h2>No tools found</h2>}
                  titleSize="m"
                />
              </EuiPanel>
            );
          } else {
            const pinnedTools = filteredTools.filter((tool) => tool.pinned);
            const unpinnedTools = filteredTools.filter((tool) => !tool.pinned);

            return (
              <VList style={{ height: "100%" }}>
                {pinnedTools.map((tool, idx, arr) => {
                  const isLastItem = idx === arr.length - 1;
                  return (
                    <Fragment key={tool.id}>
                      <ToolCard {...tool} onDelete={() => undefined} />
                      {!isLastItem && <EuiSpacer />}
                    </Fragment>
                  );
                })}

                {pinnedTools.length > 0 && unpinnedTools.length > 0 && (
                  <EuiHorizontalRule />
                )}

                {unpinnedTools.map((tool, idx, arr) => {
                  const isLastItem = idx === arr.length - 1;
                  return (
                    <Fragment key={tool.id}>
                      <ToolCard {...tool} onDelete={() => undefined} />
                      {!isLastItem && <EuiSpacer />}
                    </Fragment>
                  );
                })}
              </VList>
            );
          }
        })()}
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
